import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface UseHashScrollOptions {
  /** Comportamiento del scroll (default: 'smooth') */
  behavior?: ScrollBehavior;
  /** Offset en píxeles para evitar quedar tapado por el navbar sticky (default: 80) */
  offset?: number;
  /** Retardo antes de intentar el scroll, para esperar a que componentes lazy se monten (default: 100) */
  delay?: number;
}

/**
 * Hook que escucha cambios de hash en la URL y hace scroll suave al elemento
 * con ese id. Maneja la entrada inicial con hash y los clicks dentro de la SPA.
 *
 * Usado en `AbogadosLayout` para preservar deep-links históricos como
 * `/abogados#pricing`, `/abogados#faq`, etc.
 */
export function useHashScroll({
  behavior = 'smooth',
  offset = 80,
  delay = 100,
}: UseHashScrollOptions = {}) {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, '');
    if (!id) return;

    const timer = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior });
    }, delay);

    return () => window.clearTimeout(timer);
  }, [hash, pathname, behavior, offset, delay]);
}
